import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, ChevronDown, Factory, Loader2, Info, Search, Plus, Upload } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { AssetDetailPanel } from '../../../components/AssetDetailPanel';
import { AssetFormModal } from '../../../components/AssetFormModal';
import { assetService } from '../../../services/activos/assetService';
import { usePermissions } from '../../../hooks/usePermissions';
import { useFactory } from '../../../context/FactoryContext';
import { documentService } from '../../../services/documentos/documentService';

function buildHierarchy(assets) {
  const hierarchies = {};
  if (!assets || !Array.isArray(assets) || assets.length === 0) return hierarchies;

  const normalizeAsset = (asset) => ({
    id: asset.ID !== undefined ? asset.ID : asset.id,
    parentId: asset.ParentID !== undefined ? asset.ParentID : asset.ParentId !== undefined ? asset.ParentId : asset.parentId !== undefined ? asset.parentId : asset.parent_id,
    code: asset.Code !== undefined ? asset.Code : asset.code,
    name: asset.Name !== undefined ? asset.Name : asset.name,
    location: asset.Location !== undefined ? asset.Location : asset.location,
    factory: asset.Factory !== undefined ? asset.Factory : asset.factory,
    ...asset,
  });

  const normalizedAssets = assets.map(normalizeAsset);
  const assetMap = new Map();
  normalizedAssets.forEach((asset) => {
    if (asset.id != null) assetMap.set(asset.id, { ...asset, children: [] });
  });

  const rootNodes = normalizedAssets.filter(
    (asset) => asset.id != null && (asset.parentId == null || asset.parentId === null || asset.parentId === '')
  );

  const buildTree = (asset) => {
    const node = assetMap.get(asset.id);
    if (!node) return null;
    const children = normalizedAssets
      .filter((child) => child.parentId != null && child.parentId === asset.id)
      .map((child) => buildTree(child))
      .filter(Boolean);
    if (children.length > 0) node.children = children;
    return node;
  };

  const sortChildren = (node) => {
    if (node.children?.length) {
      node.children.sort((a, b) =>
        (a.code || '').toString().localeCompare((b.code || '').toString(), undefined, { numeric: true, sensitivity: 'base' })
      );
      node.children.forEach(sortChildren);
    }
  };

  rootNodes.forEach((rootAsset) => {
    const tree = buildTree(rootAsset);
    if (tree) {
      const factoryCode = rootAsset.code || rootAsset.factory || `ROOT_${rootAsset.id}`;
      hierarchies[factoryCode] = tree;
    }
  });
  Object.values(hierarchies).forEach(sortChildren);
  return hierarchies;
}

function getPathToAsset(hierarchies, assetId) {
  if (!hierarchies || !assetId) return [];
  const targetId = Number(assetId);

  const path = [];

  const dfs = (node) => {
    if (!node) return false;
    path.push(node);
    if (Number(node.id) === targetId) {
      return true;
    }
    if (node.children && node.children.length > 0) {
      for (const child of node.children) {
        if (dfs(child)) return true;
      }
    }
    path.pop();
    return false;
  };

  for (const root of Object.values(hierarchies)) {
    path.length = 0;
    if (dfs(root)) {
      return [...path];
    }
  }

  return [];
}

const AssetManagement = () => {
  const queryClient = useQueryClient();
  const { permissions, checkFactoryAccess } = usePermissions();
  const { selectedFactory } = useFactory();

  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState(null); // 'create' | 'edit' | null
  const [assetToEdit, setAssetToEdit] = useState(null);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 1024);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadDocumentType, setUploadDocumentType] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    setSelectedAssetId(null);
    setExpandedNodes(new Set());
    setSearchQuery('');
  }, [selectedFactory]);

  const { data: assets = [], isLoading: loading, isError: isError, error } = useQuery({
    queryKey: ['assets'],
    queryFn: () => assetService.getAssets(),
    retry: 1,
  });

  const hierarchies = useMemo(() => {
    const allHierarchies = buildHierarchy(assets);
    if (selectedFactory && allHierarchies[selectedFactory]) {
      return { [selectedFactory]: allHierarchies[selectedFactory] };
    }
    return allHierarchies;
  }, [assets, selectedFactory]);

  const {
    data: selectedAssetDetails,
    isLoading: loadingDetails,
  } = useQuery({
    queryKey: ['asset', selectedAssetId],
    queryFn: () => assetService.getAsset(selectedAssetId),
    enabled: !!selectedAssetId,
    retry: 1,
  });

  const { data: documentFeatures } = useQuery({
    queryKey: ['documentFeatures'],
    queryFn: () => documentService.getDocumentFeatures(),
    enabled: isUploadModalOpen,
  });

  const documentTypes = documentFeatures?.document_types ?? [];

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const createMutation = useMutation({
    mutationFn: (assetData) => assetService.createAsset(assetData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      setIsFormModalOpen(false);
      setFormMode(null);
      setAssetToEdit(null);
    },
    onError: (err) => {
      console.error('Error creating asset:', err);
      throw err;
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => assetService.updateAsset(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['asset', assetToEdit] });
      setIsFormModalOpen(false);
      setFormMode(null);
      setAssetToEdit(null);
    },
    onError: (err) => {
      console.error('Error updating asset:', err);
      throw err;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => assetService.deleteAsset(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      setSelectedAssetId(null);
    },
    onError: (err) => {
      console.error('Error deleting asset:', err);
      const msg = err.response?.data?.message || err.message || 'Error al eliminar el activo';
      if (msg.includes('no se puede eliminar un activo que tiene hijos')) {
        alert('No se puede eliminar este activo porque tiene activos hijos asociados. Elimine primero los activos hijos.');
      } else {
        alert(`Error al eliminar el activo: ${msg}`);
      }
    },
  });

  const handleOpenCreateModal = () => {
    setFormMode('create');
    setAssetToEdit(null);
    setIsFormModalOpen(true);
  };

  const handleOpenEditModal = (asset) => {
    setFormMode('edit');
    setAssetToEdit(asset?.id ?? asset?.ID);
    setIsFormModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsFormModalOpen(false);
    setFormMode(null);
    setAssetToEdit(null);
  };

  const handleDeleteAsset = async (assetId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este activo?')) {
      try {
        await deleteMutation.mutateAsync(assetId);
      } catch (_) {
        // Error ya mostrado en onError
      }
    }
  };

  const handleAssetSelect = (assetId, event) => {
    if (event?.target?.closest?.('.detail-button')) {
      event.preventDefault();
      event.stopPropagation();
    }
    setSelectedAssetId(assetId);
  };

  const handleCloseUploadModal = () => {
    if (isUploading) return;
    setUploadDocumentType('');
    setUploadDescription('');
    setUploadFile(null);
    setUploadError(null);
    setUploadProgress(0);
    setIsUploadModalOpen(false);
  };

  const handleUploadFileChange = (e) => {
    const file = e.target.files?.[0];
    setUploadFile(file || null);
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    setUploadError(null);

    if (!uploadDocumentType.trim()) {
      setUploadError('Document type is required');
      return;
    }
    if (!uploadFile) {
      setUploadError('Please select a file to upload');
      return;
    }
    if (!selectedAssetDetails) {
      setUploadError('No asset selected');
      return;
    }

    const assetId = selectedAssetDetails.ID ?? selectedAssetDetails.id;
    if (!assetId) {
      setUploadError('Asset ID is missing');
      return;
    }

    const assetTechCode =
      selectedAssetDetails.TechCode ??
      selectedAssetDetails.tech_code ??
      selectedAssetDetails.techCode ??
      '';

    if (!assetTechCode) {
      setUploadError('Asset technical code is missing');
      return;
    }

    const path = getPathToAsset(hierarchies, assetId);
    const level1 = path[0]?.code || '';
    const level2 = path[1]?.code || '';

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('AssetID', String(assetId));
      formData.append('AssetTechCode', String(assetTechCode));
      formData.append('HierarchicalLevel[1]', level1);
      formData.append('HierarchicalLevel[2]', level2);
      formData.append('DocumentType', uploadDocumentType);
      formData.append('Description', uploadDescription || '');
      formData.append('document', uploadFile);

      await documentService.uploadAssetDocument(formData, ({ percent }) => setUploadProgress(percent));

      await queryClient.invalidateQueries({ queryKey: ['asset', selectedAssetId] });
      handleCloseUploadModal();
    } catch (err) {
      const serverMsg = err.response?.data?.message ?? err.response?.data?.error ?? err.message;
      const status = err.response?.status;
      const debugMsg = status ? `[${status}] ${serverMsg}` : serverMsg;
      console.error('[UploadAssetDocument] Error:', { err, response: err.response });
      setUploadError(debugMsg || 'Failed to upload document');
      setUploadProgress(0);
    } finally {
      setIsUploading(false);
    }
  };

  const toggleNode = (nodeId) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  };

  const expandAll = () => {
    const ids = new Set();
    const collect = (n) => {
      ids.add(n.id);
      n.children?.forEach(collect);
    };
    Object.values(hierarchies).forEach(collect);
    setExpandedNodes(ids);
  };

  const collapseAll = () => setExpandedNodes(new Set());

  const getLevelColor = (level) => {
    switch (level) {
      case 0: return 'text-blue-400';
      case 1: return 'text-green-400';
      case 2: return 'text-yellow-400';
      default: return 'text-gray-300';
    }
  };

  const filterNode = (node) => {
    if (!node) return null;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matches = (node.code || '').toLowerCase().includes(q) || (node.location || '').toLowerCase().includes(q);
      if (matches) return { ...node, children: node.children?.map(filterNode).filter(Boolean) };
      if (node.children?.length) {
        const filtered = node.children.map(filterNode).filter(Boolean);
        if (filtered.length) return { ...node, children: filtered };
      }
      return null;
    }
    return { ...node, children: node.children?.map(filterNode).filter(Boolean) };
  };

  const renderTreeNode = (node, level = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    const isSelected = selectedAssetId === node.id;
    const indent = level * 16;
    const basePadding = 5;

    return (
      <div key={node.id}>
        <div
          className={`flex items-center gap-1 px-1 py-0.5 hover:bg-gray-800/50 rounded cursor-pointer group transition-colors ${
            isSelected ? 'bg-blue-500/20 border-l-2 border-blue-500' : ''
          }`}
          style={{ paddingLeft: `${basePadding + indent}px` }}
          onClick={() => {
            if (hasChildren) toggleNode(node.id);
            handleAssetSelect(node.id);
          }}
        >
          {hasChildren ? (
            isExpanded ? <ChevronDown size={12} className="text-gray-400 flex-shrink-0" /> : <ChevronRight size={12} className="text-gray-400 flex-shrink-0" />
          ) : (
            <div className="w-3 flex-shrink-0" />
          )}
          <span
            className={`flex-1 min-w-0 truncate text-xs transition-colors ${
              isSelected ? 'text-blue-400 font-semibold' : getLevelColor(level)
            }`}
            onClick={(e) => {
              e.stopPropagation();
              handleAssetSelect(node.id, e);
            }}
          >
            {node.code}
          </span>
        </div>
        {hasChildren && isExpanded && (
          <div>{node.children.map((child) => renderTreeNode(child, level + 1))}</div>
        )}
      </div>
    );
  };

  const renderFullTree = () => {
    return (
      <div className="space-y-0.5">
        {Object.keys(hierarchies)
          .sort()
          .map((factoryCode) => {
            const factory = hierarchies[factoryCode];
            const filtered = filterNode(factory);
            return filtered ? renderTreeNode(filtered, 0) : null;
          })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="animate-spin text-blue-500" size={48} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-red-500 text-lg mb-4">{error?.message || 'Error al cargar activos'}</p>
        </div>
      </div>
    );
  }

  if (Object.keys(hierarchies).length === 0 && !loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center text-gray-500">
          <p className="text-lg mb-4">No hay datos disponibles</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="mb-4">
        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
          <Factory size={32} />
          Gestión de Activos
        </h1>
        <p className="text-gray-400 mt-2">Explorar la estructura jerárquica y gestionar activos (crear, editar, eliminar)</p>
      </div>

      <div className="flex h-[calc(100vh-200px)] gap-4 flex-1 min-h-0 px-2">
        {/* Sidebar: Árbol */}
        <div
          className={`flex-shrink-0 bg-gray-900/50 rounded-lg border border-gray-800 overflow-hidden flex flex-col ${
            !isMobile ? 'w-64' : 'w-full lg:w-72'
          }`}
        >
          <div className="px-3 py-2 border-b border-gray-800 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-white flex items-center gap-1.5">
                <Factory size={16} />
                Jerarquía
              </h2>
              <p className="text-xs text-gray-400 mt-0.5">Selecciona un activo</p>
            </div>
            <div className="flex gap-1">
              <button type="button" onClick={expandAll} className="p-1 hover:bg-gray-800 rounded text-gray-400 hover:text-white transition-colors" title="Expandir todo">
                <ChevronDown size={14} />
              </button>
              <button type="button" onClick={collapseAll} className="p-1 hover:bg-gray-800 rounded text-gray-400 hover:text-white transition-colors" title="Colapsar todo">
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
          <div className="px-2 py-1.5 border-b border-gray-800">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
              <input
                type="text"
                placeholder="Buscar..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-7 pr-2 py-1.5 bg-gray-800 border border-gray-700 rounded text-white text-xs placeholder-gray-500 focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto py-1">
            {renderFullTree()}
          </div>
        </div>

        {/* Panel derecho: Detalles + CRUD */}
        {!isMobile && (
          <div className="flex-1 min-w-0 flex flex-col">
            {selectedAssetId ? (
              <>
                <div className="mb-2 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setIsUploadModalOpen(true)}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                  >
                    <Upload size={16} />
                    Subir documento
                  </button>
                  <button
                    type="button"
                    onClick={handleOpenCreateModal}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                  >
                    <Plus size={16} />
                    Añadir hijo
                  </button>
                </div>
                <div className="flex-1 min-h-0">
                  <AssetDetailPanel
                    assetId={selectedAssetId}
                    assetDetails={selectedAssetDetails}
                    loading={loadingDetails}
                    onClose={() => setSelectedAssetId(null)}
                    onEdit={handleOpenEditModal}
                    onDelete={handleDeleteAsset}
                    isMobile={false}
                  />
                </div>
              </>
            ) : (
              <Card className="h-full flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <Info size={48} className="mx-auto mb-4 opacity-50" />
                  <p className="text-lg">Selecciona un activo</p>
                  <p className="text-sm mt-2">Haz clic en un elemento del árbol</p>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Mobile: overlay con detalles */}
        {isMobile && selectedAssetId && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm">
            <div className="h-full flex flex-col">
              <div className="p-2 bg-gray-900/90 border-b border-gray-800 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsUploadModalOpen(true)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                >
                  <Upload size={16} />
                  Subir documento
                </button>
                <button
                  type="button"
                  onClick={handleOpenCreateModal}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                >
                  <Plus size={16} />
                  Añadir hijo
                </button>
              </div>
              <div className="flex-1 min-h-0">
                <AssetDetailPanel
                  assetId={selectedAssetId}
                  assetDetails={selectedAssetDetails}
                  loading={loadingDetails}
                  onClose={() => setSelectedAssetId(null)}
                  onEdit={handleOpenEditModal}
                  onDelete={handleDeleteAsset}
                  isMobile
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <AssetFormModal
        isOpen={isFormModalOpen}
        mode={formMode}
        assetId={assetToEdit}
        parentId={formMode === 'create' ? selectedAssetId : null}
        onClose={handleCloseModal}
        onSuccess={() => {}}
        onCreate={(data) => createMutation.mutateAsync(data)}
        onUpdate={(data) => updateMutation.mutateAsync(data)}
      />

      {isUploadModalOpen && selectedAssetDetails && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-gray-800 rounded-2xl border border-gray-700 shadow-xl w-full max-w-lg overflow-hidden flex flex-col relative z-[101]">
            <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-2xl font-semibold text-white flex items-center gap-2">
                Subir documento de activo
              </h2>
              <button
                type="button"
                onClick={handleCloseUploadModal}
                className="p-2 rounded-lg hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
                disabled={isUploading}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleUploadSubmit} className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-4">
                {uploadError && (
                  <div className="rounded-lg border border-red-800 bg-red-900/20 p-4">
                    <p className="text-red-300 text-sm">{uploadError}</p>
                  </div>
                )}

                <div className="text-xs text-gray-400 space-y-0.5">
                  <p>
                    <span className="font-semibold text-gray-200">Asset ID:</span>{' '}
                    {selectedAssetDetails.ID ?? selectedAssetDetails.id}
                  </p>
                  <p>
                    <span className="font-semibold text-gray-200">Tech code:</span>{' '}
                    {selectedAssetDetails.TechCode ??
                      selectedAssetDetails.tech_code ??
                      selectedAssetDetails.techCode ??
                      '—'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Document Type <span className="text-red-400">*</span>
                  </label>
                  <select
                    value={uploadDocumentType}
                    onChange={(e) => setUploadDocumentType(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isUploading}
                    required
                  >
                    <option value="">Select document type</option>
                    {documentTypes.map((dt) => (
                      <option key={dt.id} value={dt.name}>
                        {dt.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={uploadDescription}
                    onChange={(e) => setUploadDescription(e.target.value)}
                    rows="3"
                    className="w-full px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    placeholder="Optional description"
                    disabled={isUploading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    File <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="file"
                    accept=".pdf,application/pdf,image/*,.doc,.docx"
                    onChange={handleUploadFileChange}
                    className="w-full px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-600 file:text-white file:cursor-pointer hover:file:bg-blue-700"
                    disabled={isUploading}
                    required
                  />
                  {uploadFile && (
                    <p className="mt-1 text-sm text-gray-400 truncate">{uploadFile.name}</p>
                  )}
                </div>

                {isUploading && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm text-gray-400">
                      <span>Subiendo archivo...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-gray-700 overflow-hidden">
                      <div
                        className="h-full bg-blue-600 transition-all duration-300 ease-out"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCloseUploadModal}
                  className="px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-gray-300 text-sm font-medium hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isUploading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg border border-blue-600 bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                  disabled={isUploading}
                >
                  {isUploading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Upload
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetManagement;
